import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { AuthContext } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import {
  getGetEventUrl,
  getGetMatchesUrl,
  getGetTicketsUrl,
  getPredictionHistoryUrl,
  getPredictUrl,
} from '../../../api/generated/worldcup/worldcup/worldcup.ts';
import { DEFAULT_WORLDCUP_SCENARIO_KEY, getWorldcupMockScenario } from '../mock/worldcupFixtures';
import { buildWorldcupPageViewModel } from '../model/worldcupMappers';
import { createEmptyWorldcupPageViewModel } from '../model/worldcupViewModel';

const MAX_TICKETS_PER_MATCH = 3;
const HISTORY_LIMIT = 50;
const IS_DEV_BUILD = process.env.NODE_ENV === 'development';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const DEFAULT_WORLDCUP_ERROR_MESSAGE = '월드컵 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.';

const extractErrorMessage = (payload, fallbackMessage = DEFAULT_WORLDCUP_ERROR_MESSAGE) => {
  if (!payload) {
    return fallbackMessage;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  return payload.message || payload.error || payload.msg || fallbackMessage;
};

export function useWorldcupEventPage(options = {}) {
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken } = useContext(AuthContext);
  const { showErrorToast } = useToast();
  const accessToken = useAccessToken();
  const allowMockMode = IS_DEV_BUILD;
  const scenarioKey = options.scenarioKey ?? DEFAULT_WORLDCUP_SCENARIO_KEY;
  const scenario = useMemo(() => getWorldcupMockScenario(scenarioKey), [scenarioKey]);
  const emptyPageViewModel = useMemo(() => createEmptyWorldcupPageViewModel(), []);
  const initialMode = allowMockMode ? options.initialMode ?? 'real' : 'real';
  const [mode, setModeState] = useState(initialMode);
  const [mockPageViewModel, setMockPageViewModel] = useState(scenario.pageViewModel);
  const [selectedRoundCode, setSelectedRoundCode] = useState(() =>
    initialMode === 'mock' ? scenario.pageViewModel.currentRoundCode : null,
  );
  const [predictionDrafts, setPredictionDrafts] = useState({});
  const [expandedMatchId, setExpandedMatchId] = useState(null);
  const isRealMode = mode === 'real';
  const setMode = useCallback((nextMode) => {
    if (!allowMockMode) {
      setModeState('real');
      return;
    }

    setModeState(nextMode);
  }, [allowMockMode]);

  const fetchWorldcupWithToken = useCallback(async (...args) => {
    try {
      return await fetchWithToken(...args);
    } catch (error) {
      console.error('backend api: worldcup authenticated request failed', { error });
      throw error;
    }
  }, [fetchWithToken]);

  const fetchWorldcupPublic = useCallback(async (url) => {
    const response = await fetch(url);
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(extractErrorMessage(payload));
    }

    return payload;
  }, []);

  const eventKey = isRealMode ? `${API_SERVER_DOMAIN}${getGetEventUrl()}` : null;
  const ticketsKey = isRealMode && accessToken ? [`${API_SERVER_DOMAIN}${getGetTicketsUrl()}`, accessToken] : null;
  const historyKey =
    isRealMode && accessToken
      ? [`${API_SERVER_DOMAIN}${getPredictionHistoryUrl({ offset: 0, limit: HISTORY_LIMIT })}`, accessToken]
      : null;
  const matchesKey =
    isRealMode && accessToken && selectedRoundCode
      ? [`${API_SERVER_DOMAIN}${getGetMatchesUrl({ roundCode: selectedRoundCode })}`, accessToken]
      : null;

  const { data: eventOverviewResponse, error: eventOverviewError, mutate: mutateEventOverview } = useSWR(eventKey, fetchWorldcupPublic, {
    dedupingInterval: 0,
    revalidateOnMount: true,
  });
  const { data: ticketStatusResponse, error: ticketStatusError, mutate: mutateTicketStatus } = useSWR(ticketsKey, fetchWorldcupWithToken, {
    dedupingInterval: 0,
    revalidateOnMount: true,
  });
  const { data: predictionHistoryResponse, error: predictionHistoryError, mutate: mutatePredictionHistory } = useSWR(historyKey, fetchWorldcupWithToken, {
    dedupingInterval: 0,
    revalidateOnMount: true,
  });
  const { data: selectedRoundMatchListResponse, error: selectedRoundMatchesError, mutate: mutateSelectedRoundMatches } = useSWR(
    matchesKey,
    fetchWorldcupWithToken,
    {
      dedupingInterval: 0,
      revalidateOnMount: true,
    },
  );

  const realPageViewModel = useMemo(() => {
    if (!isRealMode) {
      return emptyPageViewModel;
    }

    return buildWorldcupPageViewModel({
      eventOverviewResponse,
      ticketStatusResponse,
      matchListResponses: selectedRoundMatchListResponse ? [selectedRoundMatchListResponse] : [],
      predictionHistoryResponse,
    });
  }, [
    emptyPageViewModel,
    eventOverviewResponse,
    isRealMode,
    predictionHistoryResponse,
    selectedRoundMatchListResponse,
    ticketStatusResponse,
  ]);

  const pageViewModel = isRealMode ? realPageViewModel : mockPageViewModel;

  useEffect(() => {
    setPredictionDrafts({});
    setExpandedMatchId(null);
  }, [mode]);

  useEffect(() => {
    if (!isRealMode) {
      setMockPageViewModel(scenario.pageViewModel);
      setSelectedRoundCode(scenario.pageViewModel.currentRoundCode);
    }
  }, [isRealMode, scenario]);

  useEffect(() => {
    if (!isRealMode) {
      return;
    }

    if (realPageViewModel.currentRoundCode && selectedRoundCode == null) {
      setSelectedRoundCode(realPageViewModel.currentRoundCode);
      return;
    }

    const hasSelectedTab = realPageViewModel.tabs.some((tab) => tab.roundCode === selectedRoundCode);

    if (!hasSelectedTab && realPageViewModel.currentRoundCode) {
      setSelectedRoundCode(realPageViewModel.currentRoundCode);
    }
  }, [isRealMode, realPageViewModel.currentRoundCode, realPageViewModel.tabs, selectedRoundCode]);

  useEffect(() => {
    const worldcupError = eventOverviewError || ticketStatusError || predictionHistoryError || selectedRoundMatchesError;

    if (!worldcupError) {
      return;
    }

    showErrorToast(worldcupError.message || DEFAULT_WORLDCUP_ERROR_MESSAGE);
  }, [eventOverviewError, predictionHistoryError, selectedRoundMatchesError, showErrorToast, ticketStatusError]);

  const selectedRoundSection = useMemo(() => {
    return pageViewModel.roundSections.find((section) => section.roundCode === selectedRoundCode) ?? null;
  }, [pageViewModel.roundSections, selectedRoundCode]);

  const tabs = useMemo(() => {
    return pageViewModel.tabs.map((tab) => ({
      ...tab,
      isSelected: tab.roundCode === selectedRoundCode,
    }));
  }, [pageViewModel.tabs, selectedRoundCode]);

  const getMaxTicketCount = (card) => {
    return Math.min(MAX_TICKETS_PER_MATCH, pageViewModel.ticketSummary.ticketBalance);
  };

  const getPredictionDraft = (matchId) => {
    return predictionDrafts[matchId] ?? { selectedTeamId: null, ticketCount: 0 };
  };

  const selectPredictionTeam = (matchId, teamId) => {
    setPredictionDrafts((current) => {
      const previousDraft = current[matchId];
      const nextTicketCount = previousDraft?.ticketCount ?? 0;

      return {
        ...current,
        [matchId]: {
          selectedTeamId: teamId,
          ticketCount: nextTicketCount,
        },
      };
    });
  };

  const togglePredictionPanel = (matchId) => {
    setExpandedMatchId((current) => {
      const isClosing = current === matchId;

      if (isClosing) {
        setPredictionDrafts((drafts) => {
          const nextDrafts = { ...drafts };
          delete nextDrafts[matchId];
          return nextDrafts;
        });
      }

      return isClosing ? null : matchId;
    });
  };

  const adjustPredictionTicketCount = (matchId, delta) => {
    const targetCard = pageViewModel.roundSections.flatMap((section) => section.matches).find((card) => card.matchId === matchId);

    if (!targetCard) {
      return;
    }

    const maxTicketCount = getMaxTicketCount(targetCard);

    setPredictionDrafts((current) => {
      const previousDraft = current[matchId] ?? { selectedTeamId: null, ticketCount: 0 };

      return {
        ...current,
        [matchId]: {
          ...previousDraft,
          ticketCount: maxTicketCount > 0 ? clamp(previousDraft.ticketCount + delta, 0, maxTicketCount) : 0,
        },
      };
    });
  };

  const submitPrediction = (matchId) => {
    const targetCard = pageViewModel.roundSections.flatMap((section) => section.matches).find((card) => card.matchId === matchId);
    const draft = getPredictionDraft(matchId);

    if (!targetCard || targetCard.cardStatus !== 'open' || targetCard.myPrediction.hasPrediction) {
      return;
    }

    if (!draft.selectedTeamId || draft.ticketCount < 1 || draft.ticketCount > pageViewModel.ticketSummary.ticketBalance) {
      return;
    }

    if (isRealMode) {
      const submitRealPrediction = async () => {
        try {
          const response = await fetch(`${API_SERVER_DOMAIN}${getPredictUrl()}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              matchId,
              pickedTeamId: draft.selectedTeamId,
              ticketCount: draft.ticketCount,
            }),
          });
          const payload = await response.json().catch(() => null);

          if (!response.ok || payload?.result === 'fail' || payload?.result === 'FAIL') {
            showErrorToast(extractErrorMessage(payload, '예측 제출에 실패했어요. 다시 시도해 주세요.'));
            return;
          }

          setPredictionDrafts((current) => {
            const nextDrafts = { ...current };
            delete nextDrafts[matchId];
            return nextDrafts;
          });
          setExpandedMatchId(null);

          await Promise.all([
            mutateSelectedRoundMatches(),
            mutateTicketStatus(),
            mutatePredictionHistory(),
            mutateEventOverview(),
          ]);
        } catch (error) {
          console.error('backend api: error POST /worldcup/predict', { error });
          showErrorToast('예측 제출 중 오류가 발생했어요. 다시 시도해 주세요.');
        }
      };

      submitRealPrediction();
      return;
    }

    setMockPageViewModel((current) => {
      const nextTicketBalance = current.ticketSummary.ticketBalance - draft.ticketCount;
      const nextRoundSections = current.roundSections.map((section) => ({
        ...section,
        matches: section.matches.map((card) => {
          if (card.matchId !== matchId) {
            return card;
          }

          const isHomePick = card.homeTeam.teamId === draft.selectedTeamId;
          const nextHomeTicketSum = card.homeTeam.ticketSum + (isHomePick ? draft.ticketCount : 0);
          const nextAwayTicketSum = card.awayTeam.ticketSum + (!isHomePick ? draft.ticketCount : 0);
          const nextTotalTicketCount = nextHomeTicketSum + nextAwayTicketSum;
          const nextTotalPoolPoint = nextTotalTicketCount * card.pointsPerTicket;
          const nextHomeRate = nextTotalTicketCount > 0 ? Math.round((nextHomeTicketSum / nextTotalTicketCount) * 100) : null;
          const nextAwayRate = nextTotalTicketCount > 0 ? Math.round((nextAwayTicketSum / nextTotalTicketCount) * 100) : null;
          const pickedTicketSum = isHomePick ? nextHomeTicketSum : nextAwayTicketSum;
          const nextEstimatedPayoutPerTicket =
            pickedTicketSum > 0 ? Math.floor(nextTotalPoolPoint / pickedTicketSum) : 0;

          return {
            ...card,
            badge: { label: '진행중', tone: 'progress' },
            totalTicketCount: nextTotalTicketCount,
            totalPoolPoint: nextTotalPoolPoint,
            estimatedPayoutPerTicket: nextEstimatedPayoutPerTicket,
            homeTeam: {
              ...card.homeTeam,
              ticketSum: nextHomeTicketSum,
              pickRate: nextHomeRate,
              isMyPick: isHomePick,
            },
            awayTeam: {
              ...card.awayTeam,
              ticketSum: nextAwayTicketSum,
              pickRate: nextAwayRate,
              isMyPick: !isHomePick,
            },
            myPrediction: {
              pickedTeamId: draft.selectedTeamId,
              ticketCount: draft.ticketCount,
              result: 'picked',
              earnedPoint: 0,
              hasPrediction: true,
            },
          };
        }),
      }));

      return {
        ...current,
        ticketSummary: {
          ...current.ticketSummary,
          ticketBalance: nextTicketBalance,
        },
        roundSections: nextRoundSections,
        predictionHistory: [
          {
            predictionId: Date.now(),
            eventId: current.event.eventId,
            matchId,
            pickedTeamId: draft.selectedTeamId,
            ticketCount: draft.ticketCount,
            resultStatus: 'PENDING',
            earnedPoint: 0,
            createdAt: new Date().toISOString(),
            settledAt: null,
          },
          ...current.predictionHistory,
        ],
      };
    });

    setPredictionDrafts((current) => {
      const nextDrafts = { ...current };
      delete nextDrafts[matchId];
      return nextDrafts;
    });
    setExpandedMatchId(null);
  };

  return {
    allowMockMode,
    mode,
    setMode,
    event: pageViewModel.event,
    tabs,
    ticketSummary: pageViewModel.ticketSummary,
    predictionHistory: pageViewModel.predictionHistory,
    selectedRoundCode,
    setSelectedRoundCode,
    selectedRoundSection,
    pageViewModel,
    scenarioKey,
    expandedMatchId,
    togglePredictionPanel,
    getPredictionDraft,
    getMaxTicketCount,
    selectPredictionTeam,
    adjustPredictionTicketCount,
    submitPrediction,
  };
}
